import React, { useState } from 'react';
import { Sun, Moon, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import ProductCard from './ProductCard';

const TabbedResults = ({ aiRecommendations, getNykaaSearchUrl, getTiraSearchUrl }) => {
  const [selectedOptions, setSelectedOptions] = useState({});

  // Initialize selected options based on recommended_option or default to "budget"
  React.useEffect(() => {
    const initOptions = {};
    [...(aiRecommendations.morning_routine || []), ...(aiRecommendations.evening_routine || [])].forEach(item => {
      const key = `${item.step}-${item.category}`;
      initOptions[key] = item.recommended_option || 'budget';
    });
    setSelectedOptions(initOptions);
  }, [aiRecommendations]);

  const handleOptionChange = (step, category, option) => {
    const key = `${step}-${category}`;
    setSelectedOptions(prev => ({ ...prev, [key]: option }));
  };

  // Calculate total cost based on currently selected options
  const calculateCurrentCost = () => {
    const allItems = [...aiRecommendations.morning_routine, ...aiRecommendations.evening_routine];
    const uniqueProducts = new Map();

    allItems.forEach(item => {
      const key = `${item.step}-${item.category}`;
      const selectedOption = selectedOptions[key] || item.recommended_option || 'budget';

      // Check if item has options structure
      if (item.options && typeof item.options === 'object') {
        const productData = item.options[selectedOption];
        const productKey = productData.product_name;

        if (!uniqueProducts.has(productKey)) {
          uniqueProducts.set(productKey, productData.price || 0);
        }
      } else {
        // Old format - backward compatibility
        const productKey = item.product_name;
        if (!uniqueProducts.has(productKey)) {
          uniqueProducts.set(productKey, item.price || 0);
        }
      }
    });

    return Array.from(uniqueProducts.values()).reduce((sum, price) => sum + price, 0);
  };

  // Count unique product categories (not product names)
  const countUniqueProducts = () => {
    const allItems = [...aiRecommendations.morning_routine, ...aiRecommendations.evening_routine];
    const uniqueCategories = new Set();

    allItems.forEach(item => {
      const key = `${item.step}-${item.category}`;
      const selectedOption = selectedOptions[key] || item.recommended_option || 'budget';

      // Check if item has options structure
      if (item.options && typeof item.options === 'object') {
        const productData = item.options[selectedOption];
        uniqueCategories.add(productData.product_name);
      } else {
        uniqueCategories.add(item.product_name);
      }
    });

    return uniqueCategories.size;
  };

  const renderProductWithOptions = (item, variant) => {
    const key = `${item.step}-${item.category}`;
    const selectedOption = selectedOptions[key] || item.recommended_option || 'budget';

    // Handle both new format (with options) and old format (flat structure)
    const hasOptions = item.options && typeof item.options === 'object';

    console.log(`🔍 Rendering ${item.category} - Has options:`, hasOptions, 'Item:', item);

    if (!hasOptions) {
      // Old format - backward compatibility
      return (
        <ProductCard
          key={key}
          step={item.step}
          name={item.category}
          product={item.product_name}
          example={item.product_name}
          why={item.why}
          price={item.price}
          quantity={item.quantity}
          duration={item.duration}
          badges={item.badges}
          usageNote={item.usage_note}
          searchUrl={getNykaaSearchUrl(item.product_name)}
          tiraUrl={getTiraSearchUrl && getTiraSearchUrl(item.product_name)}
          variant={variant}
        />
      );
    }

    // New format with options
    const productData = item.options[selectedOption];

    return (
      <ProductCard
        key={key}
        step={item.step}
        name={item.category}
        product={productData.product_name}
        example={productData.product_name}
        why={productData.why}
        price={productData.price}
        quantity={productData.quantity}
        duration={productData.duration}
        badges={productData.badges}
        usageNote={productData.usage_note}
        searchUrl={getNykaaSearchUrl(productData.product_name)}
        tiraUrl={getTiraSearchUrl && getTiraSearchUrl(productData.product_name)}
        variant={variant}
        hasOptions={true}
        selectedOption={selectedOption}
        onOptionChange={(option) => handleOptionChange(item.step, item.category, option)}
      />
    );
  };


  return (
    <>
      {/* Analysis Card */}
      <motion.div
        className="mb-8 animate-fade-in"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-violet-50 via-white to-pink-50 border-2 border-violet-200 shadow-xl">
          {/* Analysis Text */}
          {aiRecommendations.analysis && (
            <p className="text-center text-xs sm:text-sm lg:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6">
              {aiRecommendations.analysis}
            </p>
          )}

          {/* Summary Stats Grid */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
            <div className="text-center p-2 sm:p-3 lg:p-4 rounded-xl glass-strong border border-violet-100">
              <p className="text-[10px] sm:text-xs lg:text-sm text-gray-600 mb-0.5 sm:mb-1 lg:mb-2">Skin Type</p>
              <h3 className="text-sm sm:text-lg lg:text-2xl font-bold bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">
                {aiRecommendations.skin_type}
              </h3>
            </div>
            <div className="text-center p-2 sm:p-3 lg:p-4 rounded-xl glass-strong border border-violet-100">
              <p className="text-[10px] sm:text-xs lg:text-sm text-gray-600 mb-0.5 sm:mb-1 lg:mb-2">Total Products</p>
              <h3 className="text-sm sm:text-lg lg:text-2xl font-bold bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">
                {countUniqueProducts()}
              </h3>
            </div>
            <div className="text-center p-2 sm:p-3 lg:p-4 rounded-xl glass-strong border border-violet-100">
              <p className="text-[10px] sm:text-xs lg:text-sm text-gray-600 mb-0.5 sm:mb-1 lg:mb-2">Total Investment</p>
              <h3 className="text-sm sm:text-lg lg:text-2xl font-bold bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">
                ₹{calculateCurrentCost().toLocaleString()}
              </h3>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Morning Routine Section */}
      <section className="space-y-4 sm:space-y-6 mb-8 sm:mb-12">
        {/* Morning Header Card */}
        <motion.div
          className="p-4 sm:p-5 lg:p-6 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-xl rounded-2xl"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
              <Sun className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-0.5 sm:mb-1">Morning Routine</h2>
              <p className="text-xs sm:text-sm lg:text-base text-white/90">Start your day with protection and glow</p>
            </div>
          </div>
        </motion.div>

        {/* Morning Products */}
        <div className="space-y-3 sm:space-y-4">
          {aiRecommendations.morning_routine.map((item, idx) =>
            renderProductWithOptions(item, 'morning')
          )}
        </div>
      </section>

      {/* Evening Routine Section */}
      <section className="space-y-4 sm:space-y-6">
        {/* Evening Header Card */}
        <motion.div
          className="p-4 sm:p-5 lg:p-6 bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0 shadow-xl rounded-2xl"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
              <Moon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-0.5 sm:mb-1">Evening Routine</h2>
              <p className="text-xs sm:text-sm lg:text-base text-white/90">Wind down and repair while you sleep</p>
            </div>
          </div>
        </motion.div>

        {/* Evening Products */}
        <div className="space-y-3 sm:space-y-4">
          {aiRecommendations.evening_routine.map((item, idx) =>
            renderProductWithOptions(item, 'evening')
          )}
        </div>
      </section>

      {/* Timeline Section - Directly Below Routines */}
      {aiRecommendations.expected_results && (
        <motion.div
          className="mb-6 sm:mb-8 animate-fade-in delay-500"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="glass-strong rounded-2xl p-4 sm:p-6 lg:p-8 shadow-2xl border-2 border-violet-200">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-violet-600" />
              Expected Results Timeline
            </h3>
            <div className="space-y-3 sm:space-y-4">
              <motion.div
                className="bg-white rounded-xl p-3 sm:p-4 lg:p-5 shadow-md border border-violet-100"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-violet-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm glow-violet">
                    2w
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-sm sm:text-base text-gray-800 mb-1 sm:mb-2">After 2 Weeks</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{aiRecommendations.expected_results['2_weeks']}</p>
                  </div>
                </div>
              </motion.div>
              <motion.div
                className="bg-white rounded-xl p-3 sm:p-4 lg:p-5 shadow-md border border-pink-100"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-violet-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm glow-pink">
                    6w
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-sm sm:text-base text-gray-800 mb-1 sm:mb-2">After 6 Weeks</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{aiRecommendations.expected_results['6_weeks']}</p>
                  </div>
                </div>
              </motion.div>
              <motion.div
                className="bg-white rounded-xl p-3 sm:p-4 lg:p-5 shadow-md border border-violet-100"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-violet-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm glow-gradient">
                    3m
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-sm sm:text-base text-gray-800 mb-1 sm:mb-2">After 3 Months</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{aiRecommendations.expected_results['3_months']}</p>
                  </div>
                </div>
              </motion.div>
              <div className="p-3 sm:p-4 glass-violet rounded-xl border-2 border-violet-200 shadow-md">
                <p className="text-xs sm:text-sm text-gray-700">
                  <span className="font-bold bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">💪 Stay Consistent:</span> Results take time! Use products daily as recommended. Take progress photos every 2 weeks to track improvement.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default TabbedResults;
